import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Building2, Users2, Contact2, FileCheck2, FileText, ArrowLeft,
  CheckCircle2, AlertTriangle, Clock, Eye, Loader2, Download,
} from "lucide-react";

const STATUS_STYLE = {
  draft: { label: "Draft", color: "bg-slate-500/10 text-slate-400 border-slate-500/30" },
  submitted: { label: "Awaiting Review", color: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  needs_revision: { label: "Needs Revision", color: "bg-rose-500/10 text-rose-500 border-rose-500/30" },
  approved: { label: "Approved", color: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
};

export default function SuperAdminOnboardingSubmissions() {
  const [list, setList] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [revisionNotes, setRevisionNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const params = filter === "all" ? {} : { status: filter };
      const { data } = await API.get("/onboarding", { params });
      setList(data);
    } catch {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchList();
    API.get("/onboarding-catalog/documents").then((r) => setCatalog(r.data)).catch(() => {});
  }, [fetchList]);

  const openSubmission = async (id) => {
    try {
      const { data } = await API.get(`/onboarding/${id}`);
      setActive(data);
    } catch {
      toast.error("Failed to load submission");
    }
  };

  const approve = async () => {
    if (!active) return;
    setBusy(true);
    try {
      await API.post(`/onboarding/${active.id}/approve`);
      toast.success("Approved. Client promoted to Fund Manager.");
      setActive(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const submitRevision = async () => {
    if (!active || !revisionNotes.trim()) {
      toast.error("Please provide revision notes");
      return;
    }
    setBusy(true);
    try {
      await API.post(`/onboarding/${active.id}/request-revision`, { notes: revisionNotes.trim() });
      toast.success("Revision requested");
      setRevisionOpen(false);
      setRevisionNotes("");
      setActive(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // Detail View
  if (active) {
    const le = active.legal_entity || {};
    return (
      <div className="space-y-6 max-w-5xl" data-testid="sa-onboarding-detail">
        <Button variant="ghost" size="sm" onClick={() => setActive(null)} data-testid="back-to-list-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to list
        </Button>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">Onboarding Submission</p>
            <h1 className="font-heading text-3xl font-bold tracking-tight mt-2">{active.user_name}</h1>
            <p className="text-sm text-muted-foreground mt-1">{active.user_email}</p>
            <div className="flex items-center gap-2 mt-3">
              <Badge variant="outline" className={`${STATUS_STYLE[active.status]?.color} border text-xs`}>{STATUS_STYLE[active.status]?.label || active.status}</Badge>
              {active.submitted_at && <span className="text-xs text-muted-foreground">Submitted {new Date(active.submitted_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
            </div>
          </div>
          {active.status === "submitted" && (
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRevisionOpen(true)} className="border-rose-500/40 text-rose-500" data-testid="request-revision-btn">
                <AlertTriangle className="w-4 h-4 mr-2" /> Request Revision
              </Button>
              <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />} Approve & Promote
              </Button>
            </div>
          )}
        </div>

        {/* Legal Entity */}
        <DetailSection icon={Building2} title="Legal Entity">
          <KV label="Legal Name" value={le.legal_name} />
          <KV label="Incorporation Number" value={le.incorporation_number} />
          <KV label="Country" value={le.country_of_incorporation} />
          <KV label="Company Email" value={le.company_email} />
          <KV label="Company Phone" value={le.company_phone} />
          <KV label="Company Website" value={le.company_website} />
          <KV label="Correspondence Address" value={le.correspondence_address} full />
        </DetailSection>

        {/* Signatories */}
        <DetailSection icon={Users2} title={`Signatories (${active.signatories?.length || 0})`}>
          {(active.signatories || []).map((s, i) => (
            <PartyDetail key={i} title={`Signatory ${i + 1}`} party={s} />
          ))}
        </DetailSection>

        {/* Contacts */}
        <DetailSection icon={Contact2} title={`Points of Contact (${active.contacts?.length || 0})`}>
          {(active.contacts || []).map((c, i) => (
            <PartyDetail key={i} title={`Contact ${i + 1}`} party={c} />
          ))}
        </DetailSection>

        {/* Documents */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2"><FileCheck2 className="w-5 h-5 text-primary" /> Documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {catalog.map((section) => (
              <div key={section.section}>
                <p className="text-sm font-semibold mb-2">{section.section}</p>
                <div className="space-y-1.5">
                  {section.items.map((item) => {
                    const doc = active.documents?.[item.key];
                    return (
                      <div key={item.key} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2" data-testid={`sa-doc-${item.key}`}>
                        <FileText className={`w-4 h-4 ${doc ? "text-emerald-500" : "text-muted-foreground"}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{item.label}</p>
                          {doc && <p className="text-xs text-muted-foreground truncate">{doc.file_name}</p>}
                        </div>
                        {doc ? (
                          doc.file_missing ? (
                            <Badge
                              variant="outline"
                              className="bg-rose-500/10 text-rose-500 border-rose-500/40 text-[10px] gap-1"
                              data-testid={`sa-doc-missing-${item.key}`}
                              title={`Uploaded on ${doc.uploaded_at || "unknown"} but the file is no longer on disk (expected: ${doc.stored_name || "unknown"}). Ask the client to re-upload.`}
                            >
                              <AlertTriangle className="w-3 h-3" /> File missing on disk
                            </Badge>
                          ) : (
                            <DownloadDocButton
                              submissionId={active.id}
                              docKey={item.key}
                              fileName={doc.file_name}
                            />
                          )
                        ) : (
                          <Badge variant="outline" className="bg-slate-500/10 text-slate-400 border-slate-500/30 text-[10px]">Missing</Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Revision Dialog */}
        <Dialog open={revisionOpen} onOpenChange={setRevisionOpen}>
          <DialogContent data-testid="revision-dialog">
            <DialogHeader>
              <DialogTitle>Request Revisions</DialogTitle>
              <DialogDescription>The client will see these notes and can update their submission.</DialogDescription>
            </DialogHeader>
            <Textarea rows={5} value={revisionNotes} onChange={(e) => setRevisionNotes(e.target.value)} placeholder="What needs to be updated?" data-testid="revision-notes-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevisionOpen(false)}>Cancel</Button>
              <Button onClick={submitRevision} disabled={busy || !revisionNotes.trim()} data-testid="submit-revision-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Send Revision Request
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-6" data-testid="sa-onboarding-page">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Onboarding Submissions</h1>
          <p className="text-sm text-muted-foreground mt-1">Review and approve prospective client onboarding forms.</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="submitted">Awaiting Review</SelectItem>
            <SelectItem value="needs_revision">Needs Revision</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Legal Entity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-12 text-muted-foreground">No submissions yet.</TableCell></TableRow>
              ) : list.map((s) => (
                <TableRow key={s.id} data-testid={`row-${s.id}`}>
                  <TableCell className="font-medium">{s.user_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.user_email}</TableCell>
                  <TableCell className="text-sm">{s.legal_entity?.legal_name || <span className="text-muted-foreground/60">—</span>}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`${STATUS_STYLE[s.status]?.color} border text-[10px]`}>
                      {STATUS_STYLE[s.status]?.label || s.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{s.updated_at ? new Date(s.updated_at).toLocaleString("en-US", { month: "long", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openSubmission(s.id)} data-testid={`view-${s.id}`}>
                      <Eye className="w-4 h-4 mr-1.5" /> View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function DetailSection({ icon: Icon, title, children }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2"><Icon className="w-5 h-5 text-primary" /> {title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
      </CardContent>
    </Card>
  );
}

function PartyDetail({ title, party }) {
  return (
    <div className="md:col-span-2 rounded-lg border border-border bg-secondary/20 p-4">
      <p className="text-sm font-medium mb-3">{title}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <KV label="Name" value={party.name} />
        <KV label="Title" value={party.title} />
        <KV label="Email" value={party.email} />
        <KV label="LinkedIn" value={party.linkedin || "—"} />
        <KV label="Phone" value={party.phone} />
        <KV label="Mobile" value={party.mobile} />
      </div>
    </div>
  );
}

function KV({ label, value, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm mt-1 break-words">{value || <span className="text-muted-foreground/60">—</span>}</p>
    </div>
  );
}


function DownloadDocButton({ submissionId, docKey, fileName }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      const resp = await API.get(
        `/onboarding/${submissionId}/documents/${docKey}`,
        { responseType: "blob" }
      );
      const blob = resp.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || `${docKey}.bin`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download document");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={handle}
      disabled={busy}
      data-testid={`sa-download-${docKey}`}
    >
      {busy ? (
        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
      ) : (
        <Download className="w-3.5 h-3.5 mr-1.5" />
      )}
      Download
    </Button>
  );
}
